import { Box, Avatar } from '@mui/material';
import { Icon, Utils, ReadFlow, ThemeContext } from '@gv/triage-components';
import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';

import { useAppSelector } from 'store';
import { ChatRequestType } from 'types';
import * as Common from 'pages/office/inbox/styles';
import { selectAuthForGVTalk } from 'store/slices/auth';
import { useRealTimeChat } from 'context/real-time-chat';
import { useLazyDetailsQuery } from 'store/api/gv-chats';
import { useReadUntilMessageMutation } from 'store/api/gv-messages';
import { parseMessage } from 'pages/office/inbox/chat/chat-room/config';
import {
  useTyping,
  useMessages,
  useUnreadCount,
  useChatListEvents,
  useChatDetailsEvent,
} from 'hooks';

import * as Styles from './styles';

const { parseContact, getChatUserName } = Utils.Helpers;
const { formatDate } = Utils.Date;

interface RealTimeChatProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const RealTimeChat = ({ isOpen, onToggle }: RealTimeChatProps) => {
  const { isDarkTheme } = useContext(ThemeContext);
  const { setSelectedChat, selectedChat: contextSelectedChat } =
    useRealTimeChat();
  const isInitialMount = useRef(true);
  const prevIsOpen = useRef(isOpen);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const gvTalkAuth = useAppSelector(selectAuthForGVTalk);
  const chatId = contextSelectedChat?.chatId
    ? String(contextSelectedChat.chatId)
    : undefined;

  const { data: chatData } = useChatDetailsEvent({ id: chatId! }, !chatId);

  // Fetch chat list when no chat is selected
  const {
    data: chatListResponse,
    refetch: refetchChatList,
    isLoading: chatListLoading,
  } = useChatListEvents(
    {
      page: 1,
      limit: 50,
      type: ChatRequestType.All,
    },
    !!chatId // Skip if a chat is selected
  );

  const { data: unreadCount = {}, refetch: refetchUnreadCount } =
    useUnreadCount();

  const [readUntilMessage] = useReadUntilMessageMutation();

  // Refetch chat list when unread counts change to update individual chat unread badges
  useEffect(() => {
    if (!chatId && isOpen && Object.keys(unreadCount).length > 0) {
      // Refetch chat list when unread counts change to update badges in real-time
      const timer = setTimeout(() => {
        refetchChatList();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [unreadCount, chatId, isOpen, refetchChatList]);

  // Calculate total unread count across all chats
  const totalUnreadCount = useMemo(() => {
    if (!unreadCount || Object.keys(unreadCount).length === 0) {
      return 0;
    }
    return Object.values(unreadCount).reduce(
      (sum, count) => sum + (typeof count === 'number' ? count : 0),
      0
    );
  }, [unreadCount]);

  const [fetchChatDetails] = useLazyDetailsQuery();

  const currentChat = useMemo(
    () => (!chatId ? undefined : chatData),
    [chatData, chatId]
  );

  const {
    onReact,
    onRetry,
    onDeleteFile,
    onPinMessage,
    onTopReached,
    onMarkUnread,
    onEditMessage,
    onViewMessage,
    isNewerLoading,
    onBottomReached,
    onDeleteMessage,
    handleSendMessage,
    messages: messagesData,
    isLoading: messagesLoading,
  } = useMessages(currentChat, null, undefined);

  const { typers, onType } = useTyping(currentChat);

  // Refetch unread count when chat is opened/closed or when messages change
  useEffect(() => {
    if (chatId && messagesData?.length) {
      // When a chat is opened and messages are loaded:
      // 1. Refetch immediately to get current state
      // 2. Refetch again after readUntil completes (readUntil runs after 2 seconds)
      refetchUnreadCount();

      const timer = setTimeout(() => {
        refetchUnreadCount();
      }, 3000); // After readUntil completes (2 seconds) + buffer to ensure counts are updated
      return () => clearTimeout(timer);
    }
  }, [chatId, messagesData?.length, refetchUnreadCount]);

  // Refetch unread count when chat is closed or when going back to list
  // Use a longer delay to ensure readUntil has completed (readUntil runs after 2 seconds)
  useEffect(() => {
    if (!chatId && isOpen) {
      // When returning to chat list, refetch to get updated counts
      // Wait longer (3 seconds) to ensure readUntil has completed marking messages as read
      const timer = setTimeout(() => {
        refetchUnreadCount();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [chatId, isOpen, refetchUnreadCount]);

  const messages = useMemo(() => {
    if (!messagesData?.length) {
      return [];
    }
    return messagesData.map((msg) => parseMessage(msg));
  }, [messagesData]);

  // Setup mention users for @mentions functionality
  const mentionUsers = useMemo(() => {
    if (!currentChat?.members?.length) {
      return undefined;
    }

    const { authUserId, authClientId } = gvTalkAuth;

    const filtered = currentChat.members.filter((value) => {
      if (authUserId) {
        return authUserId !== value.userId;
      } else if (authClientId) {
        return authClientId !== value.clientId;
      }
      return true;
    });

    const items = filtered.map((member) => ({
      id: String(member.id),
      display: getChatUserName(parseContact(member) ?? {}),
    }));

    return items.length
      ? [{ display: `All`, id: 'mention-all-members' }, ...items]
      : [];
  }, [currentChat?.members, gvTalkAuth]);

  const onMentionClick = useCallback(() => {
    // Handle mention click if needed
  }, []);

  // Wrap handleSendMessage to ensure chatId is passed explicitly
  // Since we're not in a route with chatId, we need to pass it explicitly
  const wrappedHandleSendMessage = useCallback(
    async (values: any, resetForm?: () => void) => {
      if (!chatId) {
        return;
      }
      // Pass chatId explicitly as the 4th parameter (chatIdValue)
      await handleSendMessage(values, resetForm, undefined, chatId);
    },
    [handleSendMessage, chatId]
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      setShouldAnimate(false);
      prevIsOpen.current = isOpen;
      if (isOpen) {
        setHasBeenOpened(true);
      }
      return;
    }

    if (prevIsOpen.current !== isOpen) {
      if (isOpen) {
        setHasBeenOpened(true);
      }
      setShouldAnimate(true);
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 500);
      prevIsOpen.current = isOpen;
      return () => clearTimeout(timer);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen]);

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatLastMessageTime = (
    dateString: string | undefined
  ): string | null => {
    if (!dateString) {
      return null;
    }
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      // Show relative time for recent messages
      if (diffMins < 1) {
        return 'Just now';
      }
      if (diffMins < 60) {
        return `${diffMins}m ago`;
      }
      if (diffHours < 24) {
        return `${diffHours}h ago`;
      }
      if (diffDays < 7) {
        return `${diffDays}d ago`;
      }

      // For older messages, show date and time
      return formatDate(dateString, 'MMM DD, h:mm A');
    } catch {
      return formatDate(dateString, 'MMM DD, h:mm A');
    }
  };

  // Mark messages as read when exiting chat to ensure unread count updates
  const markMessagesAsRead = useCallback(async () => {
    if (!chatId || !messagesData?.length) {
      return;
    }

    try {
      // Find the last message ID
      const lastMessage = messagesData
        .slice()
        .reverse()
        .find((msg) => msg.id && !msg.isError && !msg.isSending);

      if (lastMessage?.id) {
        // Mark all messages up to the last one as read
        await readUntilMessage({
          chatId: chatId,
          flow: ReadFlow.Main,
          messageId: lastMessage.id,
        }).unwrap();
      }
    } catch (error) {
      // Silently handle errors - readUntil might have already been called
      console.error('Error marking messages as read:', error);
    }
  }, [chatId, messagesData, readUntilMessage]);

  const handleClose = async () => {
    // Mark messages as read before closing to ensure unread count updates
    await markMessagesAsRead();

    // Reset selected chat when closing
    setSelectedChat(null);

    // Refetch unread count after a delay to ensure it's updated
    setTimeout(() => {
      refetchUnreadCount();
    }, 1000);

    onToggle();
  };

  const handleBack = async () => {
    // Mark messages as read before going back to ensure unread count updates
    await markMessagesAsRead();

    // Reset selected chat when going back
    setSelectedChat(null);

    // Refetch unread count after a delay to ensure it's updated
    setTimeout(() => {
      refetchUnreadCount();
    }, 1000);
  };

  // Process chat list with unread counts
  const chatList = useMemo(() => {
    const rows = chatListResponse?.rows || [];
    if (!rows.length) {
      return [];
    }
    return rows.map((chatItem: (typeof rows)[0]) => ({
      ...chatItem,
      unreadCount: unreadCount[chatItem.id] || 0,
    }));
  }, [chatListResponse?.rows, unreadCount]);

  // Handle chat selection from list
  const handleChatSelect = useCallback(
    async (selectedChatItem: (typeof chatList)[0]) => {
      try {
        // Fetch chat details to get member information
        const result = await fetchChatDetails({ id: selectedChatItem.id });
        const chatDetails = result.data;

        if (!chatDetails) {
          return;
        }

        // Find the other member (not the current user)
        const { authUserId, authClientId } = gvTalkAuth;
        const otherMember = chatDetails.members?.find((member) => {
          if (authUserId) {
            return member.userId !== authUserId;
          } else if (authClientId) {
            return member.clientId !== authClientId;
          }
          return true;
        });

        if (otherMember) {
          const contact = parseContact(otherMember);
          const memberPic =
            (otherMember as any).profile_pic ||
            (otherMember as any).avatar ||
            undefined;
          setSelectedChat({
            userId: otherMember.id,
            chatId: selectedChatItem.id,
            userName: getChatUserName(contact ?? {}),
            userImageUrl: memberPic != null ? String(memberPic) : undefined,
            userRole: contact?.role != null ? String(contact.role) : undefined,
          });
        } else {
          // If no other member found (group chat), use chat name
          const chatProps = Utils.GVTalk.getChatCardProps(
            chatDetails,
            gvTalkAuth
          );
          setSelectedChat({
            userId: 0,
            userRole: undefined,
            userImageUrl: undefined,
            chatId: selectedChatItem.id,
            userName: chatProps.chat.title || 'Chat',
          });
        }
      } catch (error) {
        console.error('Error selecting chat:', error);
      }
    },
    [gvTalkAuth, setSelectedChat, fetchChatDetails]
  );

  const userName = contextSelectedChat?.userName || 'User';
  const userRole = contextSelectedChat?.userRole;
  const userImageUrl = contextSelectedChat?.userImageUrl;

  return (
    <>
      <Styles.FloatingButton
        isOpen={isOpen}
        isDarkTheme={isDarkTheme}
        onClick={isOpen ? handleClose : onToggle}
        aria-label={isOpen ? 'Close Real Time Chat' : 'Open Real Time Chat'}
      >
        <Icon.Chat />
        {!isOpen && totalUnreadCount > 0 && (
          <Styles.NotificationBadge isDarkTheme={isDarkTheme}>
            {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
          </Styles.NotificationBadge>
        )}
      </Styles.FloatingButton>

      {(hasBeenOpened || isOpen) && (
        <Styles.ChatPanel
          isOpen={isOpen}
          isDarkTheme={isDarkTheme}
          shouldAnimate={shouldAnimate}
        >
          <Styles.ChatHeader isDarkTheme={isDarkTheme}>
            <Box
              sx={{ flex: 1, gap: 1.5, display: 'flex', alignItems: 'center' }}
            >
              {contextSelectedChat && (
                <Styles.BackButton
                  aria-label="Back"
                  onClick={handleBack}
                  isDarkTheme={isDarkTheme}
                >
                  <Icon.ChevronLeft />
                </Styles.BackButton>
              )}
              {contextSelectedChat ? (
                <Box
                  sx={{
                    flex: 1,
                    gap: 1.5,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {userImageUrl ? (
                    <Avatar src={userImageUrl} sx={{ width: 32, height: 32 }} />
                  ) : (
                    <Styles.AvatarPlaceholder isDarkTheme={isDarkTheme}>
                      {getInitials(userName)}
                    </Styles.AvatarPlaceholder>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Styles.HeaderTitle isDarkTheme={isDarkTheme}>
                      {userName}
                    </Styles.HeaderTitle>
                    {userRole && (
                      <Styles.HeaderSubtitle isDarkTheme={isDarkTheme}>
                        {userRole}
                      </Styles.HeaderSubtitle>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    gap: 1,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Styles.HeaderTitle isDarkTheme={isDarkTheme}>
                    Team Communication
                  </Styles.HeaderTitle>
                  {totalUnreadCount > 0 && (
                    <Styles.UnreadCountBadge isDarkTheme={isDarkTheme}>
                      {totalUnreadCount > 99 ? '99+' : totalUnreadCount} unread
                    </Styles.UnreadCountBadge>
                  )}
                </Box>
              )}
            </Box>
            <Styles.CloseButton onClick={handleClose} isDarkTheme={isDarkTheme}>
              <Icon.Close />
            </Styles.CloseButton>
          </Styles.ChatHeader>

          {contextSelectedChat && chatId ? (
            <Styles.ChatContent>
              <Common.ChatRoom
                isGV
                task={{}}
                groupByDate
                typers={typers}
                onType={onType}
                isAdmin={false}
                onRetry={onRetry}
                onReact={onReact}
                messages={messages}
                hideForwardIcon={true}
                parseOptions={gvTalkAuth}
                isLoading={messagesLoading}
                onTopReached={onTopReached}
                onMarkUnread={onMarkUnread}
                onPinMessage={onPinMessage}
                mentionUsers={mentionUsers}
                onDeleteFile={onDeleteFile}
                defaultScrollToBottom={true}
                onViewMessage={onViewMessage}
                onEditMessage={onEditMessage}
                placeholder="Start type here"
                isNewerLoading={isNewerLoading}
                onMentionClick={onMentionClick}
                onBottomReached={onBottomReached}
                onDeleteMessage={onDeleteMessage}
                handleSendMessage={wrappedHandleSendMessage}
              />
            </Styles.ChatContent>
          ) : (
            <Styles.ChatContent>
              {chatListLoading ? (
                <Styles.EmptyState isDarkTheme={isDarkTheme}>
                  <Styles.EmptyStateText isDarkTheme={isDarkTheme}>
                    Loading chats...
                  </Styles.EmptyStateText>
                </Styles.EmptyState>
              ) : chatList.length > 0 ? (
                <Styles.ChatListContainer isDarkTheme={isDarkTheme}>
                  {chatList.map((chatItem: (typeof chatList)[0]) => {
                    const chatProps = Utils.GVTalk.getChatCardProps(
                      chatItem,
                      gvTalkAuth
                    );
                    const unread = chatItem.unreadCount || 0;
                    const chatTitle = chatProps.chat.title || 'Chat';
                    const firstMember = chatProps.chat.firstMember;
                    const avatarUrl =
                      firstMember &&
                      ((firstMember as any).profile_pic ||
                        (firstMember as any).avatar ||
                        (firstMember as any).photo);

                    // Get last message data
                    const lastMessage = (chatItem as any).lastMessage;
                    const lastMessageText =
                      lastMessage?.text || lastMessage?.msg_text;
                    const lastMessageDate =
                      lastMessage?.createdAt || lastMessage?.created_at;
                    const formattedDateTime =
                      formatLastMessageTime(lastMessageDate);
                    return (
                      <Styles.ChatListItem
                        key={chatItem.id}
                        isDarkTheme={isDarkTheme}
                        onClick={() => handleChatSelect(chatItem)}
                      >
                        <Styles.ChatItemAvatar>
                          {avatarUrl ? (
                            <Avatar
                              src={String(avatarUrl)}
                              sx={{ width: 40, height: 40 }}
                            />
                          ) : (
                            <Styles.AvatarPlaceholder isDarkTheme={isDarkTheme}>
                              {getInitials(chatTitle)}
                            </Styles.AvatarPlaceholder>
                          )}
                        </Styles.ChatItemAvatar>
                        <Styles.ChatItemContent>
                          <Styles.ChatItemName isDarkTheme={isDarkTheme}>
                            {chatTitle}
                          </Styles.ChatItemName>
                          {lastMessageText ? (
                            <Styles.ChatItemSubtitleContainer>
                              <Styles.ChatItemMessage isDarkTheme={isDarkTheme}>
                                {lastMessageText}
                              </Styles.ChatItemMessage>
                              {formattedDateTime && (
                                <Styles.ChatItemDateTime
                                  isDarkTheme={isDarkTheme}
                                >
                                  {formattedDateTime}
                                </Styles.ChatItemDateTime>
                              )}
                            </Styles.ChatItemSubtitleContainer>
                          ) : chatProps.chat.firstMemberName ? (
                            <Styles.ChatItemSubtitle isDarkTheme={isDarkTheme}>
                              {chatProps.chat.firstMemberName}
                            </Styles.ChatItemSubtitle>
                          ) : null}
                        </Styles.ChatItemContent>
                        {unread > 0 && (
                          <Styles.ChatItemBadge isDarkTheme={isDarkTheme}>
                            {unread > 99 ? '99+' : unread}
                          </Styles.ChatItemBadge>
                        )}
                      </Styles.ChatListItem>
                    );
                  })}
                </Styles.ChatListContainer>
              ) : (
                <Styles.EmptyState isDarkTheme={isDarkTheme}>
                  <Icon.Chat style={{ fontSize: 48, opacity: 0.3 }} />
                  <Styles.EmptyStateText isDarkTheme={isDarkTheme}>
                    No chats yet. Click on a message icon to start chatting
                  </Styles.EmptyStateText>
                </Styles.EmptyState>
              )}
            </Styles.ChatContent>
          )}
        </Styles.ChatPanel>
      )}
    </>
  );
};
